/**
 * 🔒 VAULT SEAL — DO NOT MODIFY (Feb 24 2026). See /app/memory/VAULT_SEAL.md.
 * Sealed until the user types "Upgrade Vault".
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, RefreshCw, Search, Plus, Headphones, BookOpen, Users, Folder, TrendingUp, Inbox, Check, X as XIcon, FileBadge, Eye, Download, CreditCard, ShieldCheck, Smartphone, ListChecks } from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';
import Sparkline from './Sparkline';
import GrantVaultModal from './GrantVaultModal';
import PriceTagEditor from './PriceTagEditor';

/**
 * Admin panel — flag any ebook / audiobook / audiobook series as
 * is_vault=true. Vault-flagged items are hidden from /library and only
 * appear inside /vault after the user re-enters their code.
 */
const AdminVaultManager = () => {
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [requests, setRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [reqPage, setReqPage] = useState(1);
  const [reqPageSize] = useState(10);
  const [reqTotal, setReqTotal] = useState(0);
  const [reqHasMore, setReqHasMore] = useState(false);
  const [reqSearchInput, setReqSearchInput] = useState('');
  const [reqSearch, setReqSearch] = useState('');
  const [reqStatus, setReqStatus] = useState(''); // '', 'pending', 'approved', 'rejected'
  const [reqLoading, setReqLoading] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [purchasesPending, setPurchasesPending] = useState(0);
  const [busyPurchaseId, setBusyPurchaseId] = useState('');
  const [grantModalUser, setGrantModalUser] = useState(null);
  const [busyReqId, setBusyReqId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [filter, setFilter] = useState('');
  const [showOnlyVault, setShowOnlyVault] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [grantedUsers, setGrantedUsers] = useState([]);
  const [busyDeviceUser, setBusyDeviceUser] = useState('');

  const fetchAudit = async () => {
    setAuditLoading(true);
    try {
      const [auditRes, usersRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/admin/vault/audit?limit=100`),
        authFetch(`${BACKEND_URL}/api/admin/users`),
      ]);
      if (auditRes.ok) {
        const d = await auditRes.json();
        setAuditLog(d.attempts || []);
      }
      if (usersRes.ok) {
        const d = await usersRes.json();
        setGrantedUsers((d.users || []).filter((u) => u.vault_access_granted));
      }
    } finally {
      setAuditLoading(false);
    }
  };

  const resetDevice = async (u) => {
    if (!window.confirm(
      `Reset the bound device for ${u.email}?\n\nThe next time they enter their vault code, the new device will be bound. Use this when they get a new phone or browser.`,
    )) return;
    setBusyDeviceUser(u.user_id);
    try {
      const res = await authFetch(
        `${BACKEND_URL}/api/admin/vault/reset-device/${u.user_id}`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert(d.detail || 'Reset failed');
        return;
      }
      await fetchAudit();
    } finally {
      setBusyDeviceUser('');
    }
  };

  const fetchRequests = async (page = reqPage, q = reqSearch, status = reqStatus) => {
    setReqLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(reqPageSize));
      if (q) params.set('q', q);
      if (status) params.set('status', status);
      const res = await authFetch(
        `${BACKEND_URL}/api/admin/vault/requests?${params.toString()}`,
      );
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
        setPendingCount(data.pending_count || 0);
        setReqTotal(data.total || 0);
        setReqHasMore(!!data.has_more);
      }
    } finally {
      setReqLoading(false);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const [eligibleRes, statsRes, histRes, purchaseRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/admin/vault/eligible-content`),
        authFetch(`${BACKEND_URL}/api/admin/vault/stats`),
        authFetch(`${BACKEND_URL}/api/admin/vault/stats/history?days=30`),
        authFetch(`${BACKEND_URL}/api/admin/vault/purchases`),
      ]);
      if (eligibleRes.ok) {
        const data = await eligibleRes.json();
        setItems(data.items || []);
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (histRes.ok) {
        const data = await histRes.json();
        setHistory(data.snapshots || []);
      }
      if (purchaseRes.ok) {
        const data = await purchaseRes.json();
        setPurchases(data.purchases || []);
        setPurchasesPending(data.pending_count || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  const reviewRequest = async (req, action) => {
    setBusyReqId(req.request_id);
    try {
      const res = await authFetch(
        `${BACKEND_URL}/api/admin/vault/requests/${req.request_id}/${action}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: '' }),
        },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert(d.detail || 'Action failed');
        return;
      }
      if (action === 'approve') {
        setGrantModalUser({
          user_id: req.user_id,
          email: req.user_email,
          name: req.user_name,
        });
      }
      await Promise.all([fetchItems(), fetchRequests(reqPage, reqSearch, reqStatus)]);
    } finally {
      setBusyReqId('');
    }
  };

  const reviewPurchase = async (p, action) => {
    setBusyPurchaseId(p.purchase_id);
    try {
      const res = await authFetch(
        `${BACKEND_URL}/api/admin/vault/purchases/${p.purchase_id}/${action}`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert(d.detail || 'Action failed');
        return;
      }
      if (action === 'approve') {
        // Open existing GrantVaultModal pre-targeted at this user.
        setGrantModalUser({
          user_id: p.user_id,
          email: p.user_email,
          name: p.user_email,
        });
      }
      await fetchItems();
    } finally {
      setBusyPurchaseId('');
    }
  };

  useEffect(() => {
    fetchItems();
    fetchAudit();
    fetchRequests(1, '', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce the search input → 350 ms after typing stops, commit to reqSearch.
  useEffect(() => {
    const handle = setTimeout(() => {
      setReqSearch(reqSearchInput.trim());
      setReqPage(1);
    }, 350);
    return () => clearTimeout(handle);
  }, [reqSearchInput]);

  // Re-fetch whenever the committed filters or page changes.
  useEffect(() => {
    fetchRequests(reqPage, reqSearch, reqStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqPage, reqSearch, reqStatus]);

  const toggle = async (item) => {
    setBusyId(item.id);
    try {
      const res = await authFetch(
        `${BACKEND_URL}/api/admin/vault/content/${item.id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_vault: !item.is_vault }),
        },
      );
      if (res.ok) {
        setItems((prev) =>
          prev.map((it) =>
            it.id === item.id ? { ...it, is_vault: !item.is_vault } : it,
          ),
        );
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || 'Failed to toggle vault status');
      }
    } finally {
      setBusyId('');
    }
  };

  const filtered = items.filter((it) => {
    if (showOnlyVault && !it.is_vault) return false;
    if (filter && !it.title.toLowerCase().includes(filter.toLowerCase())) return false;
    return true;
  });

  const vaultCount = items.filter((it) => it.is_vault).length;

  return (
    <div className="space-y-4" data-testid="admin-vault-manager">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Lock className="w-4 h-4 text-fuchsia-300" />
            Vault Content
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {vaultCount} of {items.length} item(s) currently in the vault. Vault items are hidden
            from the regular Library — readers see them only inside <span className="text-fuchsia-300">/vault</span> after entering their code.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => navigate('/upload?vault=1')}
            size="sm"
            className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
            data-testid="vault-upload-ebook-audio-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            <BookOpen className="w-3.5 h-3.5 mr-1" />
            <Headphones className="w-3.5 h-3.5 mr-1.5" />
            Upload Ebook / Audio to Vault
          </Button>
          <Button
            onClick={fetchItems}
            variant="outline"
            size="sm"
            className="text-gray-300 border-slate-600"
            data-testid="vault-manager-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="vault-stats-grid">
          <div className="bg-gradient-to-br from-fuchsia-600/30 to-purple-900/20 border border-fuchsia-500/40 rounded-xl p-4">
            <div className="flex items-center gap-2 text-fuchsia-300 text-xs uppercase tracking-wider font-semibold">
              <Lock className="w-3.5 h-3.5" /> Items locked
            </div>
            <p className="text-3xl font-bold text-white mt-1" data-testid="vault-stat-items">{stats.items_total}</p>
            <p className="text-[11px] text-gray-400">Hidden from Library</p>
          </div>
          <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-2 text-blue-300 text-xs uppercase tracking-wider font-semibold">
              <Users className="w-3.5 h-3.5" /> Users granted
            </div>
            <p className="text-3xl font-bold text-white mt-1" data-testid="vault-stat-users">{stats.users_granted}</p>
            <p className="text-[11px] text-gray-400">With active codes</p>
          </div>
          <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-2 text-indigo-300 text-xs uppercase tracking-wider font-semibold">
              <BookOpen className="w-3.5 h-3.5" /> Ebooks
            </div>
            <p className="text-3xl font-bold text-white mt-1">{stats.ebooks}</p>
            <p className="text-[11px] text-gray-400">In vault</p>
          </div>
          <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-2 text-purple-300 text-xs uppercase tracking-wider font-semibold">
              <Headphones className="w-3.5 h-3.5" /> Audio
            </div>
            <p className="text-3xl font-bold text-white mt-1">
              {stats.audiobooks + stats.series}
            </p>
            <p className="text-[11px] text-gray-400">
              {stats.audiobooks} standalone · {stats.series} series
            </p>
          </div>
          <div className="bg-gradient-to-br from-amber-500/30 to-orange-900/20 border border-amber-400/40 rounded-xl p-4 col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 text-amber-200 text-xs uppercase tracking-wider font-semibold">
              <TrendingUp className="w-3.5 h-3.5" /> Revenue potential
            </div>
            <div className="flex items-end justify-between gap-2 mt-1">
              <p className="text-2xl font-bold text-white" data-testid="vault-stat-revenue">
                £{Number(stats.revenue_potential_gbp || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <Sparkline
                values={history.map((s) => Number(s.revenue_potential_gbp || 0))}
                width={80}
                height={28}
              />
            </div>
            <p className="text-[11px] text-amber-200/70 mt-0.5">
              {stats.paid_items || 0} paid item(s) · catalog £{Number(stats.catalog_value_gbp || 0).toFixed(2)} × {stats.users_granted || 0} granted
              {history.length > 1 && (
                <span className="ml-1">· {history.length}-day trend</span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Vault Requests inbox */}
      <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4" data-testid="vault-requests-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h4 className="text-sm font-semibold text-white flex items-center gap-2">
            <Inbox className="w-4 h-4 text-fuchsia-300" />
            Vault Access Requests
            {pendingCount > 0 && (
              <span className="ml-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-fuchsia-600/30 text-fuchsia-200 border border-fuchsia-500/40">
                {pendingCount} pending
              </span>
            )}
          </h4>
          <Button
            onClick={() =>
              window.open(
                `${BACKEND_URL}/api/admin/vault/compliance.csv?days=30`,
                '_blank',
                'noopener,noreferrer',
              )
            }
            size="sm"
            variant="outline"
            className="text-blue-300 border-blue-500/40 hover:bg-blue-600/20"
            data-testid="vault-compliance-csv-btn"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> Compliance CSV (30 d)
          </Button>
        </div>

        {/* Search + status filter row */}
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <div className="relative flex-1 min-w-0">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <input
              type="text"
              value={reqSearchInput}
              onChange={(e) => setReqSearchInput(e.target.value)}
              placeholder="Search by email, name, reason, or request ID..."
              className="w-full bg-slate-900/80 border border-slate-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-white placeholder:text-gray-500 focus:border-fuchsia-500/50 focus:outline-none"
              data-testid="vault-requests-search-input"
            />
            {reqSearchInput && (
              <button
                onClick={() => setReqSearchInput('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                data-testid="vault-requests-search-clear"
                aria-label="Clear search"
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex gap-1 flex-wrap">
            {[
              { v: '', label: 'All' },
              { v: 'pending', label: 'Pending' },
              { v: 'approved', label: 'Approved' },
              { v: 'rejected', label: 'Rejected' },
            ].map((t) => (
              <button
                key={t.v || 'all'}
                onClick={() => { setReqStatus(t.v); setReqPage(1); }}
                className={`text-[11px] px-2.5 py-1 rounded-md border transition ${
                  reqStatus === t.v
                    ? 'bg-fuchsia-600/30 text-fuchsia-100 border-fuchsia-500/60'
                    : 'bg-slate-900/60 text-gray-400 border-slate-700 hover:text-white hover:border-slate-500'
                }`}
                data-testid={`vault-requests-status-${t.v || 'all'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {requests.length === 0 ? (
          <p className="text-xs text-gray-500 py-3 text-center" data-testid="vault-requests-empty">
            {reqLoading ? 'Loading...' : (reqSearch || reqStatus) ? 'No requests match your filters.' : 'No requests yet.'}
          </p>
        ) : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div
                key={r.request_id}
                className="flex flex-col sm:flex-row sm:items-center gap-3 bg-slate-900/60 border border-slate-700 rounded-lg p-3"
                data-testid={`vault-request-row-${r.request_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-white truncate">{r.user_email}</p>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-700 text-gray-300">
                      {r.user_role || 'user'}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-600/20 text-emerald-300 border border-emerald-500/40">
                      Age {r.age}
                    </span>
                    {r.id_doc?.filename && r.status === 'pending' && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-500/40 flex items-center gap-1" title={`Document: ${r.id_doc.original_name}`}>
                        <FileBadge className="w-2.5 h-2.5" />
                        {r.id_doc.doc_type?.replace('_', ' ') || 'ID'}
                      </span>
                    )}
                    {r.id_verified && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-600/20 text-blue-300 border border-blue-500/40 flex items-center gap-1">
                        <Check className="w-2.5 h-2.5" /> ID Verified
                      </span>
                    )}
                    {r.status === 'approved' && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-600/20 text-blue-300 border border-blue-500/40">
                        Approved
                      </span>
                    )}
                    {r.status === 'rejected' && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-600/20 text-red-300 border border-red-500/40">
                        Rejected
                      </span>
                    )}
                  </div>
                  {r.reason && (
                    <p className="text-xs text-gray-400 mt-1 line-clamp-2 italic">"{r.reason}"</p>
                  )}
                  <p className="text-[10px] text-gray-600 mt-1">
                    DOB {r.dob} · submitted {new Date(r.created_at).toLocaleDateString()}
                  </p>
                </div>
                {r.status === 'pending' && (
                  <div className="flex gap-2 shrink-0 flex-wrap">
                    {r.id_doc?.filename && (
                      <Button
                        onClick={() =>
                          window.open(
                            `${BACKEND_URL}/api/admin/vault/requests/${r.request_id}/id-doc`,
                            '_blank',
                            'noopener,noreferrer',
                          )
                        }
                        size="sm"
                        variant="outline"
                        className="text-amber-200 border-amber-500/40 hover:bg-amber-600/20"
                        data-testid={`vault-request-view-id-${r.request_id}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" /> View ID
                      </Button>
                    )}
                    <Button
                      onClick={() => reviewRequest(r, 'approve')}
                      disabled={busyReqId === r.request_id}
                      size="sm"
                      className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                      data-testid={`vault-request-approve-${r.request_id}`}
                    >
                      <Check className="w-3.5 h-3.5 mr-1" /> Approve & Issue Code
                    </Button>
                    <Button
                      onClick={() => reviewRequest(r, 'reject')}
                      disabled={busyReqId === r.request_id}
                      size="sm"
                      variant="outline"
                      className="text-red-300 border-red-500/40 hover:bg-red-600/20"
                      data-testid={`vault-request-reject-${r.request_id}`}
                    >
                      <XIcon className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination controls */}
        {reqTotal > 0 && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700/60 flex-wrap gap-2" data-testid="vault-requests-pagination">
            <p className="text-[11px] text-gray-500">
              Page <span className="text-gray-300 font-semibold">{reqPage}</span>
              {' '}of{' '}
              <span className="text-gray-300 font-semibold">
                {Math.max(1, Math.ceil(reqTotal / reqPageSize))}
              </span>
              {' · '}
              <span className="text-gray-300 font-semibold">{reqTotal}</span> total
              {(reqSearch || reqStatus) && <span className="text-fuchsia-300/80"> (filtered)</span>}
            </p>
            <div className="flex gap-1.5">
              <Button
                onClick={() => setReqPage((p) => Math.max(1, p - 1))}
                disabled={reqPage <= 1 || reqLoading}
                size="sm"
                variant="outline"
                className="text-gray-300 border-slate-600 hover:bg-slate-700/50 h-7 px-2.5 text-xs"
                data-testid="vault-requests-prev"
              >
                ← Prev
              </Button>
              <Button
                onClick={() => setReqPage((p) => p + 1)}
                disabled={!reqHasMore || reqLoading}
                size="sm"
                variant="outline"
                className="text-gray-300 border-slate-600 hover:bg-slate-700/50 h-7 px-2.5 text-xs"
                data-testid="vault-requests-next"
              >
                Next →
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4" data-testid="vault-purchases-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h4 className="text-sm font-semibold text-white flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-emerald-300" />
            Vault Access Purchases
            {purchasesPending > 0 && (
              <span className="ml-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-emerald-600/30 text-emerald-200 border border-emerald-500/40">
                {purchasesPending} to review
              </span>
            )}
          </h4>
          <span className="text-[10px] text-gray-500">
            Last 200 purchases · auto-refreshes on action
          </span>
        </div>
        {purchases.length === 0 ? (
          <p className="text-xs text-gray-500 py-3 text-center">No purchases yet.</p>
        ) : (
          <div className="space-y-2">
            {purchases.slice(0, 20).map((p) => (
              <div
                key={p.purchase_id}
                className="flex flex-col sm:flex-row sm:items-center gap-3 bg-slate-900/60 border border-slate-700 rounded-lg p-3"
                data-testid={`vault-purchase-row-${p.purchase_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-white truncate">{p.user_email}</p>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-700 text-gray-300">
                      {p.gateway === 'paypal' ? 'PAYPAL' : 'BANK · NGN'}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-600/20 text-emerald-300 border border-emerald-500/40">
                      £{Number(p.amount_gbp || 0).toFixed(2)}
                    </span>
                    {p.status === 'awaiting_admin_approval' && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-500/40">
                        Awaiting approval
                      </span>
                    )}
                    {p.status === 'paid' && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-600/20 text-blue-200 border border-blue-500/40">
                        Paid · issue code
                      </span>
                    )}
                    {p.status === 'admin_approved' && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-600/30 text-emerald-200 border border-emerald-500/40">
                        Approved
                      </span>
                    )}
                    {p.status === 'rejected' && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-600/20 text-red-300 border border-red-500/40">
                        Rejected
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1 font-mono">{p.reference}</p>
                  <p className="text-[10px] text-gray-600 mt-0.5">
                    Submitted {p.created_at ? new Date(p.created_at).toLocaleString() : '—'}
                  </p>
                </div>
                {(p.status === 'awaiting_admin_approval' || p.status === 'paid') && (
                  <div className="flex gap-2 shrink-0 flex-wrap">
                    <Button
                      onClick={() => reviewPurchase(p, 'approve')}
                      disabled={busyPurchaseId === p.purchase_id}
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`vault-purchase-approve-${p.purchase_id}`}
                    >
                      <Check className="w-3.5 h-3.5 mr-1" />
                      Approve & Issue Code
                    </Button>
                    {p.status === 'awaiting_admin_approval' && (
                      <Button
                        onClick={() => reviewPurchase(p, 'reject')}
                        disabled={busyPurchaseId === p.purchase_id}
                        size="sm"
                        variant="outline"
                        className="text-red-300 border-red-500/40 hover:bg-red-600/20"
                        data-testid={`vault-purchase-reject-${p.purchase_id}`}
                      >
                        <XIcon className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Vault Audit Log + Bound-device reset */}
      <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4" data-testid="vault-audit-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h4 className="text-sm font-semibold text-white flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-300" />
            Vault Audit Log
            <span className="ml-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-slate-700/70 text-gray-300 border border-slate-600">
              {auditLog.length} recent
            </span>
          </h4>
          <Button
            onClick={fetchAudit}
            size="sm"
            variant="outline"
            className="text-emerald-300 border-emerald-500/40 hover:bg-emerald-600/20"
            disabled={auditLoading}
            data-testid="vault-audit-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${auditLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Granted users · Bound-device reset row */}
        {grantedUsers.length > 0 && (
          <div className="mb-4">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-2 flex items-center gap-1.5">
              <Smartphone className="w-3 h-3" /> Granted users · 1 device per code
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {grantedUsers.map((u) => (
                <div
                  key={u.user_id}
                  className="flex items-center justify-between gap-2 bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2"
                  data-testid={`vault-granted-user-${u.user_id}`}
                >
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-white truncate">{u.email}</p>
                    <p className="text-[10px] text-gray-500">
                      {u.vault_device_hash ? (
                        <span className="text-emerald-400">bound · {String(u.vault_device_hash).slice(0, 10)}…</span>
                      ) : (
                        <span className="text-amber-300">no device bound yet</span>
                      )}
                    </p>
                  </div>
                  <Button
                    onClick={() => resetDevice(u)}
                    size="sm"
                    variant="outline"
                    disabled={busyDeviceUser === u.user_id || !u.vault_device_hash}
                    className="text-amber-300 border-amber-500/40 hover:bg-amber-600/20 disabled:opacity-40"
                    title={u.vault_device_hash ? 'Reset bound device' : 'Nothing to reset'}
                    data-testid={`vault-reset-device-${u.user_id}`}
                  >
                    <Smartphone className="w-3 h-3 mr-1" />
                    {busyDeviceUser === u.user_id ? '…' : 'Reset'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Audit log table */}
        <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-2 flex items-center gap-1.5">
          <ListChecks className="w-3 h-3" /> Recent unlock attempts
        </p>
        {auditLog.length === 0 ? (
          <p className="text-xs text-gray-500 py-3 text-center">No unlock attempts logged yet.</p>
        ) : (
          <div className="overflow-x-auto -mx-1 max-h-96 overflow-y-auto rounded-lg border border-slate-700">
            <table className="w-full text-[11px] sm:text-xs" data-testid="vault-audit-table">
              <thead className="bg-slate-900/80 text-left text-gray-400 sticky top-0">
                <tr>
                  <th className="px-2 py-2 font-semibold">When</th>
                  <th className="px-2 py-2 font-semibold">User</th>
                  <th className="px-2 py-2 font-semibold">Status</th>
                  <th className="px-2 py-2 font-semibold">IP</th>
                  <th className="px-2 py-2 font-semibold hidden md:table-cell">User-Agent</th>
                  <th className="px-2 py-2 font-semibold">Device</th>
                </tr>
              </thead>
              <tbody>
                {auditLog.map((a) => {
                  const statusCls =
                    a.status === 'success' || a.status === 'admin_bypass' || a.status === 'device_bound'
                      ? 'text-emerald-300 bg-emerald-600/15 border-emerald-500/40'
                      : a.status === 'reverify_required'
                      ? 'text-amber-300 bg-amber-500/15 border-amber-500/40'
                      : 'text-rose-300 bg-rose-600/15 border-rose-500/40';
                  return (
                    <tr
                      key={a.attempt_id}
                      className="border-t border-slate-700 hover:bg-slate-700/20"
                      data-testid={`vault-audit-row-${a.attempt_id}`}
                    >
                      <td className="px-2 py-1.5 text-gray-400 whitespace-nowrap">
                        {a.created_at ? new Date(a.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-gray-200 truncate max-w-[160px]">
                        {a.user_email || a.user_id}
                      </td>
                      <td className="px-2 py-1.5">
                        <span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${statusCls}`}>
                          {(a.status || 'unknown').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-gray-400 font-mono">{a.ip || '—'}</td>
                      <td className="px-2 py-1.5 text-gray-500 truncate max-w-[260px] hidden md:table-cell" title={a.user_agent}>
                        {a.user_agent || '—'}
                      </td>
                      <td className="px-2 py-1.5 text-gray-500 font-mono">
                        {a.device_id_hash ? `${String(a.device_id_hash).slice(0, 8)}…` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by title…"
            className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-fuchsia-500"
            data-testid="vault-manager-search"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-300 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={showOnlyVault}
            onChange={(e) => setShowOnlyVault(e.target.checked)}
            data-testid="vault-manager-only-vault"
          />
          Show only vault items
        </label>
      </div>

      {/* Items */}
      {loading ? (
        <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">No matching items.</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((it) => (
            <div
              key={it.id}
              className="flex items-center justify-between gap-3 bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-3"
              data-testid={`vault-row-${it.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-semibold text-white truncate">{it.title}</h4>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 px-1.5 py-0.5 bg-slate-700 rounded">
                    {it.kind === 'series' ? 'Audio Series' : it.type}
                  </span>
                  {it.is_vault && (
                    <span className="text-[10px] uppercase tracking-wider text-fuchsia-300 px-1.5 py-0.5 bg-fuchsia-600/20 border border-fuchsia-500/40 rounded flex items-center gap-1">
                      <Lock className="w-2.5 h-2.5" /> VAULT
                    </span>
                  )}
                  {it.is_vault && (
                    it.is_free || !it.price_amount ? (
                      <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-300 px-1.5 py-0.5 bg-emerald-600/20 border border-emerald-500/40 rounded">
                        FREE
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider font-bold text-amber-200 px-1.5 py-0.5 bg-amber-500/20 border border-amber-500/40 rounded">
                        £{Number(it.price_amount).toFixed(2)}
                      </span>
                    )
                  )}
                  {/* Coin price tag — separate from the GBP fiat price.
                      Lets admins set a coin paywall on Vault items so users
                      can spend their accumulated gamification coins to unlock. */}
                  <PriceTagEditor
                    contentId={it.id}
                    value={it.price_coins}
                    onSaved={(next) =>
                      setItems((prev) =>
                        prev.map((p) => (p.id === it.id ? { ...p, price_coins: next } : p))
                      )
                    }
                    size="sm"
                    testIdPrefix="vault-price"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {it.owner_email || 'system'} · id: {it.id}
                </p>
              </div>
              <Button
                onClick={() => toggle(it)}
                disabled={busyId === it.id}
                size="sm"
                variant={it.is_vault ? 'outline' : 'default'}
                className={
                  it.is_vault
                    ? 'text-orange-300 border-orange-500/50 hover:bg-orange-600/20'
                    : 'bg-fuchsia-600 hover:bg-fuchsia-700 text-white'
                }
                data-testid={`vault-toggle-${it.id}`}
              >
                {busyId === it.id ? '…' : it.is_vault ? 'Remove from Vault' : 'Add to Vault'}
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Grant code modal — opens after admin approves a request */}
      {grantModalUser && (
        <GrantVaultModal
          user={grantModalUser}
          onClose={() => setGrantModalUser(null)}
          onGranted={fetchItems}
        />
      )}
    </div>
  );
};

export default AdminVaultManager;
